import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CodeChip from './CodeChip';
import { SPEAKER_COLORS, SPEAKER_LABELS, TARGET } from './codingColors';

// One utterance: number, speaker and text (Figma "Utterance row"). `context` is a row of the ±2
// window, `target` the utterance being coded, `sheet` a coding-sheet row with its UT and RC cells.
// Speaker role and text only: never a name, a model or LLM label, or another coder's code.
const UtteranceRow = React.forwardRef(({
    number,
    speaker = 'unknown',
    text,
    variant = 'context',
    ut,
    rc,
    selected = false,
    onClick,
    ...rest
}, ref) => {
    const isTarget = variant === 'target';
    const isSheet = variant === 'sheet';
    const interactive = Boolean(onClick);

    return (
        <Box
            ref={ref}
            onClick={onClick}
            aria-current={isTarget || selected ? 'true' : undefined}
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: isSheet ? '32px 1fr auto' : '32px 1fr',
                    sm: isSheet ? '36px 72px 1fr auto' : '36px 72px 1fr',
                },
                columnGap: { xs: 1, sm: 2 },
                alignItems: 'baseline',
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: isTarget ? TARGET.border : selected ? 'primary.main' : 'transparent',
                bgcolor: isTarget ? TARGET.background : selected ? (theme) => alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                cursor: interactive ? 'pointer' : 'default',
                transition: (theme) => theme.transitions.create(['background-color', 'border-color'], { duration: theme.transitions.duration.shortest }),
                ...(interactive && !selected && { '&:hover': { bgcolor: 'action.hover' } }),
            }}
            {...rest}
        >
            <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                {number}
            </Typography>
            <Typography
                component="span"
                variant="body2"
                sx={{
                    fontWeight: 600,
                    color: SPEAKER_COLORS[speaker] || SPEAKER_COLORS.unknown,
                    gridColumn: { xs: '2', sm: 'auto' },
                    gridRow: { xs: '1', sm: 'auto' },
                }}
            >
                {SPEAKER_LABELS[speaker] || SPEAKER_LABELS.unknown}
            </Typography>
            <Typography
                component="span"
                variant="body2"
                sx={{
                    fontWeight: isTarget ? 700 : 400,
                    color: 'text.primary',
                    gridColumn: { xs: '2', sm: 'auto' },
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                }}
            >
                {text}
            </Typography>
            {isSheet && (
                <Box sx={{ display: 'flex', gap: 0.75, alignSelf: 'center', gridColumn: { xs: '3', sm: 'auto' }, gridRow: { xs: '1 / span 2', sm: 'auto' } }}>
                    <CodeChip code={ut} coded aria-label={ut ? `UT ${ut}` : 'UT not coded'} />
                    <CodeChip code={rc} coded aria-label={rc ? `RC ${rc}` : 'RC not coded'} />
                </Box>
            )}
        </Box>
    );
});

export default UtteranceRow;
